import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Typography } from '@mui/material';

// Import MBF-specific components
import StatCard from './StatCard';
import IndiaMap from './IndiaMap';
import MBFGenderStatsChart from './charts/MBFGenderStatsChart';
import MBFStudentAdminChart from './charts/MBFStudentAdminChart';
import MBFUserGrowthChart from './charts/MBFUserGrowthChart';
import MBFUserDistributionChart from './charts/MBFUserDistributionChart';
import MBFDepartmentDistributionChart from './charts/MBFDepartmentDistributionChart';
import MBFZoneStatisticsChart from './charts/MBFZoneStatisticsChart';
// Import reusable component
import { ErrorDisplay, SampleDataBadge, FullscreenChartModal } from '../common';

// Import API
import MBFDashboardAPIs from '../../../utilities/api/dashboard/MBFDashboardAPIs';

// Import sample data utilities
import { isMBFDataEmpty, getMBFSampleData } from '../../../utilities/sampleData/mbfDashboardSampleData';

// Import Indian city coordinates for map
import { getCityCoordinates, getMarkerColor } from '../../../utilities/indianCityCoordinates';

// Icons for stat cards
const TotalUsersIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
            fill="#c144ff"
        />
    </svg>
);

const NewUsersIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            fill="#e28b27"
        />
    </svg>
);

const GrowthIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"
            fill="#128d16"
        />
    </svg>
);

const FootfallIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
            d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6h-6z"
            fill="#ff448c"
        />
    </svg>
);

/**
 * MBF User Statistics Tab
 * Displays India map, stat cards, and charts for MBF Dashboard
 * 
 * @param {object} dateRange - Date range filter { startDate, endDate }
 * @param {string} selectedTL - Selected TL ID for filtering
 */
const MBFUserStatisticsTab = ({ dateRange = {}, selectedTL = '' }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [maximizedChart, setMaximizedChart] = useState(null);

    // Fetch data when dateRange, selectedTL, or refreshTrigger changes
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await MBFDashboardAPIs.getUserStatistics({
                    startDate: dateRange.startDate,
                    endDate: dateRange.endDate,
                    tlId: selectedTL,
                });
                setData(response);
            } catch (err) {
                console.error('Error fetching MBF user statistics:', err);
                setError('Failed to load user statistics');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dateRange.startDate, dateRange.endDate, selectedTL, refreshTrigger]);

    const handleRetry = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size={48} sx={{ color: '#007bff' }} />
                <Typography sx={{ color: '#666', fontSize: '14px', fontWeight: 500 }}>
                    Fetching user statistics...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return <ErrorDisplay message={error} onRetry={handleRetry} />;
    }

    // Extract data from API response with sample data fallbacks
    const totalRegisteredUsers = data?.total_registered_users || 0;
    const newUsersThisPeriod = data?.new_registered_users_this_period || 0;
    const growthPercentage = data?.growth?.percentage || '0%';
    const totalFootfall = data?.total_footfall?.count || 0;
    const footfallGrowth = data?.total_footfall?.growth_percentage || '0%';

    // Gender Stats with sample data fallback
    const rawGenderStats = data?.gender_stats || { male: 0, female: 0 };
    const isGenderStatsEmpty = rawGenderStats.male === 0 && rawGenderStats.female === 0;
    const genderStats = isGenderStatsEmpty ? getMBFSampleData('genderStats') : rawGenderStats;

    // Student vs Admin with sample data fallback
    const rawStudentVsAdmin = data?.student_vs_admin || { student_count: 0, admin_count: 0 };
    const isStudentAdminEmpty = rawStudentVsAdmin.student_count === 0 && rawStudentVsAdmin.admin_count === 0;
    const studentVsAdmin = isStudentAdminEmpty ? getMBFSampleData('studentVsAdmin') : rawStudentVsAdmin;

    // User Growth Trend with sample data fallback
    const rawUserGrowthTrend = data?.user_growth_trend || [];
    const isUserGrowthEmpty = isMBFDataEmpty(rawUserGrowthTrend, 'array');
    const userGrowthTrend = isUserGrowthEmpty ? getMBFSampleData('userGrowthTrend') : rawUserGrowthTrend;

    // TL User Distribution with sample data fallback
    const rawTLDistribution = data?.tl_user_distribution || [];
    const isTLDistributionEmpty = isMBFDataEmpty(rawTLDistribution, 'array');
    const tlUserDistribution = isTLDistributionEmpty ? getMBFSampleData('tlUserDistribution') : rawTLDistribution;

    // Department Distribution with sample data fallback
    const rawDeptDistribution = data?.distribution_across_departments || [];
    const isDeptDistributionEmpty = isMBFDataEmpty(rawDeptDistribution, 'array');
    const departmentDistribution = isDeptDistributionEmpty ? getMBFSampleData('departmentDistribution') : rawDeptDistribution;

    // Zone Statistics with sample data fallback
    const rawZoneStats = data?.zone_wise_tl_statistics || [];
    const isZoneStatsEmpty = isMBFDataEmpty(rawZoneStats, 'array');
    const zoneStatistics = isZoneStatsEmpty ? getMBFSampleData('zoneStatistics') : rawZoneStats;

    // TL Locations for Map - Use utility function with comprehensive city database
    const rawTLLocations = data?.tl_locations || [];
    const tlMarkers = rawTLLocations
        .map((tl, index) => {
            const coordinates = getCityCoordinates(tl.city_name);
            if (!coordinates) return null; // Skip if city not found
            return {
                name: tl.tl_name,
                coordinates: coordinates,
                color: getMarkerColor(index),
            };
        })
        .filter(Boolean); // Remove null entries

    // Helper function to get chart title for fullscreen modal
    const getChartTitle = (chartType) => {
        switch (chartType) {
            case 'userGrowth': return 'User Growth Trend';
            case 'userDistribution': return 'Distribution of users across TLs';
            case 'departmentDistribution': return 'Distribution Across Departments';
            case 'zoneStatistics': return 'Zone-wise TL Statistics';
            default: return '';
        }
    };

    // Helper function to render chart content in fullscreen modal
    const renderChartContent = (chartType) => {
        switch (chartType) {
            case 'userGrowth':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFUserGrowthChart data={userGrowthTrend} isSampleData={isUserGrowthEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'userDistribution':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFUserDistributionChart data={tlUserDistribution} isSampleData={isTLDistributionEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'departmentDistribution':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFDepartmentDistributionChart data={departmentDistribution} isSampleData={isDeptDistributionEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            case 'zoneStatistics':
                return (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MBFZoneStatisticsChart data={zoneStatistics} isSampleData={isZoneStatsEmpty} isFullscreen={true} />
                        </Box>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Fullscreen Chart Modal */}
            <FullscreenChartModal
                open={Boolean(maximizedChart)}
                onClose={() => setMaximizedChart(null)}
                title={getChartTitle(maximizedChart)}
            >
                {renderChartContent(maximizedChart)}
            </FullscreenChartModal>

            {/* First Row - Map and Stats */}
            <Grid container spacing={3}>
                {/* Left Side - India Map */}
                <Grid item xs={12} lg={5}>
                    <Box sx={{ height: { xs: '350px', md: '100%', lg: '480px' } }}>
                        <IndiaMap markers={tlMarkers} />
                    </Box>
                </Grid>

                {/* Right Side - Stats Grid */}
                <Grid item xs={12} lg={7}>
                    <Grid container spacing={2}>
                        {/* Row 1 - Total Users & New Users */}
                        <Grid item xs={12} sm={6}>
                            <StatCard
                                value={totalRegisteredUsers.toLocaleString()}
                                subtitle="Total Registered Users"
                                icon={<TotalUsersIcon />}
                                iconBgColor="#f0d2ff"
                                accentColor="#c144ff"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StatCard
                                value={newUsersThisPeriod.toLocaleString()}
                                subtitle="New Registered Users This Month"
                                icon={<NewUsersIcon />}
                                iconBgColor="#fbd2a3"
                                accentColor="#e28b27"
                            />
                        </Grid>

                        {/* Row 2 - Growth & Footfall */}
                        <Grid item xs={12} sm={6}>
                            <StatCard
                                value={growthPercentage}
                                subtitle="Growth"
                                suffix="MoM"
                                icon={<GrowthIcon />}
                                iconBgColor="#e0ffe0"
                                accentColor="#128d16"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <StatCard
                                value={totalFootfall.toLocaleString()}
                                subtitle="Total Footfall"
                                growth={footfallGrowth}
                                growthColor="#00a63e"
                                icon={<FootfallIcon />}
                                iconBgColor="#ffd4e4"
                                accentColor="#ff448c"
                            />
                        </Grid>

                        {/* Row 3 - Gender Stats & Student/Admin Chart */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ height: '220px' }}>
                                <MBFGenderStatsChart
                                    maleCount={genderStats.male}
                                    femaleCount={genderStats.female}
                                    isSampleData={isGenderStatsEmpty}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ height: '220px' }}>
                                <MBFStudentAdminChart
                                    studentCount={studentVsAdmin.student_count}
                                    adminCount={studentVsAdmin.admin_count}
                                    isSampleData={isStudentAdminEmpty}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Second Row - User Growth Trend and Distribution Charts */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                    <MBFUserGrowthChart
                        data={userGrowthTrend}
                        isSampleData={isUserGrowthEmpty}
                        onMaximize={() => setMaximizedChart('userGrowth')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFUserDistributionChart
                        data={tlUserDistribution}
                        isSampleData={isTLDistributionEmpty}
                        onMaximize={() => setMaximizedChart('userDistribution')}
                    />
                </Grid>
            </Grid>

            {/* Third Row - Department Distribution and Zone Statistics */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                    <MBFDepartmentDistributionChart
                        data={departmentDistribution}
                        isSampleData={isDeptDistributionEmpty}
                        onMaximize={() => setMaximizedChart('departmentDistribution')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <MBFZoneStatisticsChart
                        data={zoneStatistics}
                        isSampleData={isZoneStatsEmpty}
                        onMaximize={() => setMaximizedChart('zoneStatistics')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default MBFUserStatisticsTab;
